import React, { useState, useRef, useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import { ChevronLeft, ChevronRight, Eye, ChevronUp, ChevronDown, LayoutGrid } from 'lucide-react';
import { Template } from '../../../../types/template';
import { templateService } from '../../services/templateService';

const getTemplateThumbnail = (templateId: string): string => {
  const thumbnailMap: Record<string, string> = {
    'edu-course-intro': '/templates/course-intro.jpeg',
    'edu-tutorial': '/templates/tutorial-explainer.jpeg',
    'edu-student-testimonial': '/templates/student-testimonial.jpeg',
    'biz-pitch-deck': '/templates/pitch-deck.jpeg',
    'biz-product-demo': '/templates/product-demo.jpeg',
    'biz-company-culture': '/templates/company-culture.jpeg',
    'content-youtube-opener': '/templates/youtube-opener.jpeg',
    'content-social-promo': '/templates/social-promo.jpeg',
    'dev-code-tutorial': '/templates/code-tutorial.jpeg',
    'dev-api-docs': '/templates/api-doc.jpeg',
    'interview-intro': '/templates/candidate-intro.jpeg',
    'interview-remote-setup': '/templates/remote-interview.jpeg',
  };
  return thumbnailMap[templateId] || '/templates/custom.jpeg';
};

interface TemplateCarouselProps {
  onViewAll: () => void;
  onTemplateSelect: (template: Template) => void;
  onMakeItYours: (template: Template) => void;
  onPreview: (template: Template) => void;
}

const getAspectRatioPlatforms = (ratio: string): string => {
  const platforms: Record<string, string> = {
    '16:9': 'Best for: YouTube, Courses, Presentations',
    '9:16': 'Best for: Instagram Reels, TikTok, YouTube Shorts',
    '1:1': 'Best for: Instagram Feed, LinkedIn',
  };
  return platforms[ratio] || 'Standard aspect ratio';
};

export const TemplateCarousel: React.FC<TemplateCarouselProps> = ({
  onViewAll,
  onTemplateSelect,
  onMakeItYours,
  onPreview,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollLeftRef = useRef<number>(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const allTemplates = await templateService.getAllTemplates();
      const officialTemplates = allTemplates.filter(t => t.isOfficial);
      setTemplates(officialTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && !collapsed) {
      container.addEventListener('scroll', updateScrollButtons);
      // Restore scroll position after expanding and update button states
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = lastScrollLeftRef.current;
          updateScrollButtons();
        }
      });
      return () => container.removeEventListener('scroll', updateScrollButtons);
    }
  }, [templates, collapsed]);

  const handleToggleCollapse = () => {
    // Save scroll position before collapsing
    if (!collapsed && scrollContainerRef.current) {
      lastScrollLeftRef.current = scrollContainerRef.current.scrollLeft;
    }
    setCollapsed(!collapsed);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 280;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (loading) {
    return (
      <div className="template-carousel">
        <div className="carousel-header">
          <h3 className="carousel-title">Explore Templates</h3>
        </div>
        <div className="carousel-loading">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="carousel-skeleton-card" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="template-carousel">
      <div className={`carousel-header ${collapsed ? 'collapsed' : ''}`}>
        <h3 className="carousel-title">Explore Templates</h3>
        <div className="carousel-controls">
          <Button 
            type="link" 
            onClick={onViewAll}
            className="view-all-btn"
          >
            View all
          </Button>
          {!collapsed && (
            <div className="carousel-arrows">
              <Button
                type="text"
                icon={<ChevronLeft size={18} />}
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                className="carousel-arrow"
              />
              <Button
                type="text"
                icon={<ChevronRight size={18} />}
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                className="carousel-arrow"
              />
            </div>
          )}
          <Button
            type="text"
            icon={collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            onClick={handleToggleCollapse}
            className="carousel-collapse-btn"
          />
        </div>
      </div>
      
      {!collapsed && (
      <div className="carousel-container">
        <div 
          className="carousel-scroll" 
          ref={scrollContainerRef}
        >
          {templates.map((template) => (
            <div key={template.id} className="carousel-card" onClick={() => onTemplateSelect(template)}>
              <div className="carousel-card-thumbnail">
                <img 
                  src={getTemplateThumbnail(template.id)} 
                  alt={template.title}
                  loading="lazy"
                />
                <div className="carousel-card-overlay">
                  <Button
                    type="primary"
                    icon={<Eye size={18} />}
                    className="preview-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreview(template);
                    }}
                  >
                    Preview
                  </Button>
                </div>
                {template.aspectRatio && (
                  <Tooltip title={getAspectRatioPlatforms(template.aspectRatio)}>
                    <span className="carousel-card-badge">
                      <span className={`aspect-icon aspect-icon--${template.aspectRatio.replace(':', '-')}`} />
                      {template.aspectRatio}
                    </span>
                  </Tooltip>
                )}
              </div>
              <div className="carousel-card-content">
                <h4 className="carousel-card-title">{template.title}</h4>
                <p className="carousel-card-description">{template.description}</p>
              </div>
            </div>
          ))}
          
          <div className="carousel-card carousel-card--browse-all" onClick={onViewAll}>
            <div className="browse-all-content">
              <div className="browse-all-icon">
                <LayoutGrid size={32} />
              </div>
              <h4 className="browse-all-title">Browse All Templates</h4>
              <p className="browse-all-description">Explore our complete library of templates</p>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};
